import { useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { reviewSmsApi } from '../api/reviewSmsApi';
import { reviewKeys } from '../queryKeys';
import { useCanEditProgram, useSmsProgram } from './hooks';
import { STATUS_LABEL, isLive, statusTone, textingReadiness } from './format';
import { SetupWizard } from './SetupWizard';
import { TodayTab } from './TodayTab';
import { ConversationsTab } from './ConversationsTab';
import { PerformanceTab } from './PerformanceTab';
import styles from './TextRequests.module.css';

export type TextSubTab = 'setup' | 'today' | 'conversations' | 'performance';

const SUB_TABS: { value: TextSubTab; label: string }[] = [
  { value: 'setup', label: 'Setup' },
  { value: 'today', label: 'Today' },
  { value: 'conversations', label: 'Conversations' },
  { value: 'performance', label: 'Performance' },
];

function isSubTab(value: string | null): value is TextSubTab {
  return SUB_TABS.some((t) => t.value === value);
}

const TONE_CLASS = { live: styles.pillLive, setup: styles.pillSetup, paused: styles.pillPaused };

/**
 * Text after the visit, inside Get reviews (PRD 5 §4): Setup, Today, Conversations, Performance. The header carries
 * the program status and the pause switch; the Today tab badge shows unconfirmed visits.
 */
export function TextRequestsTab() {
  const program = useSmsProgram();
  const canEdit = useCanEditProgram();
  const view = program.data;
  // The sub-tab and the open conversation live in the URL (?sub=, ?conversation=), like the
  // Reviews tab itself, so the Today table can link straight to a thread and the link can be shared.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('sub');
  const effectiveSub: TextSubTab = isSubTab(requested)
    ? requested
    : view && isLive(view)
      ? 'today'
      : 'setup';
  const conversationId = searchParams.get('conversation');
  const go = useCallback(
    (next: TextSubTab, conversation?: string | null) =>
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('sub', next);
        if (next === 'conversations' && conversation) params.set('conversation', conversation);
        else if (next !== 'conversations' || conversation === null) params.delete('conversation');
        return params;
      }),
    [setSearchParams],
  );

  const { data: day } = useQuery({
    queryKey: reviewKeys.smsDay(program.locationId, undefined),
    queryFn: () => reviewSmsApi.getDay(program.locationId),
    enabled: !!program.locationId,
    staleTime: 30_000,
  });
  const unconfirmed = day?.unconfirmed ?? 0;
  // What the watchdog emails about (test mode left on, no uploads, setup waiting…), shown here too.
  const { data: headsUps } = useQuery({
    queryKey: reviewKeys.smsHeadsUps(program.locationId),
    queryFn: () => reviewSmsApi.listHeadsUps(program.locationId),
    enabled: !!program.locationId,
    staleTime: 60_000,
  });

  if (program.isLoading) return <SkeletonCard />;
  if (!view) return null;

  // A program can be switched on before texting is approved; the pill says what it waits on, and
  // nothing texts (tests included) until the number is live and registration approved.
  const readiness = textingReadiness(view);
  const waiting =
    view.status === 'live' && !readiness.ready
      ? readiness.numberLive
        ? 'Waiting on carrier registration'
        : 'Waiting on texting number'
      : null;
  // Test mode marks the program live so the test can text; patients wait for Go live.
  const testing = view.test.enabled && view.status === 'live' ? 'Testing, not live yet' : null;
  const tone = waiting || testing ? 'setup' : statusTone(view.status);

  return (
    <div className={styles.root}>
      <div className={styles.tabRow}>
        <div className={styles.subTabs} role="tablist" aria-label="Text requests">
          {SUB_TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={effectiveSub === t.value}
              className={clsx(styles.subTab, effectiveSub === t.value && styles.subTabActive)}
              onClick={() => go(t.value, null)}
            >
              {t.label}
              {t.value === 'today' && unconfirmed > 0 && (
                <span className={styles.countBadge} aria-label={`${unconfirmed} unconfirmed`}>
                  {unconfirmed}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className={styles.headerRight}>
          <span className={TONE_CLASS[tone]}>
            {waiting ?? testing ?? STATUS_LABEL[view.status]}
          </span>
          {view.test.enabled && <span className={styles.pillInfo}>Test mode</span>}
          {canEdit && view.status === 'live' && !view.test.enabled && (
            <Button
              variant="secondary"
              size="sm"
              isLoading={program.pause.isPending}
              onClick={() => program.pause.mutate()}
            >
              Pause texting
            </Button>
          )}
          {canEdit && view.status === 'paused' && (
            <Button
              variant="primary"
              size="sm"
              isLoading={program.resume.isPending}
              onClick={() => program.resume.mutate()}
            >
              Resume texting
            </Button>
          )}
        </div>
      </div>

      {(headsUps ?? []).map((h) => (
        <Callout
          key={h.kind}
          variant="warning"
          title={h.title}
          actions={
            <Link to={h.link.path} className={styles.textButton}>
              {h.link.label}
            </Link>
          }
        >
          {h.body}
        </Callout>
      ))}

      {/* Keyed by location: each step keeps its edits in local state, and switching location must
          never carry location A's unsaved values into a save for location B. */}
      {effectiveSub === 'setup' && (
        <SetupWizard key={program.locationId} program={program} canEdit={canEdit} />
      )}
      {effectiveSub === 'today' && (
        <TodayTab
          key={program.locationId}
          locationId={program.locationId}
          onOpenConversation={(id) => go('conversations', id)}
        />
      )}
      {effectiveSub === 'conversations' && (
        <ConversationsTab
          key={program.locationId}
          locationId={program.locationId}
          timezone={view.timing.practiceTimezone}
          selectedId={conversationId}
          onSelect={(id) => go('conversations', id)}
        />
      )}
      {effectiveSub === 'performance' && (
        <PerformanceTab key={program.locationId} locationId={program.locationId} />
      )}
    </div>
  );
}
